import { useAnonRestApi, useAuthRestApi } from '../common';
import { CSSProperties, Fragment, ReactNode } from 'react';
import { Observable } from 'rxjs';
import { IDataPackage, IDataPackageApi, IDataPackageFileDescriptor } from '@vivli/shared/features/data-package/infrastructure/interface';
import { useModalApiContext } from '@vivli/core/infrastructure/context';

export const useDataPackageApi = (): IDataPackageApi => {
    const { handleGet, handlePut, handleDelete } = useAuthRestApi();
    const modalApi = useModalApiContext();

    const anonRestApi = useAnonRestApi();

    const getDataPackage = (dataPackageId: string, anonymous?: boolean): Observable<IDataPackage> => {
        if (anonymous) {
            return getForAnonymousUser(dataPackageId);
        }

        return handleGet<IDataPackage>(`/dataPackages/${dataPackageId}`);
    };

    const getStudyDocumentsDataPackage = (dataPackageId: string, ipdDataPackageId: string): Observable<IDataPackage> => {
        return handleGet<IDataPackage>(`/dataPackages/${dataPackageId}/combined/${ipdDataPackageId}`);
    };

    const getForAnonymousUser = (dataPackageDoi: string): Observable<IDataPackage> => {
        return anonRestApi.handleGet<IDataPackage>(`/dataPackages/${dataPackageDoi}/metadata`);
    };

    const getDataPackageSubmitStatus = (dataPackageId: string): Observable<boolean> => {
        return handleGet<boolean>(`/dataPackages/${dataPackageId}/submitted`);
    };

    const updateDataPackage = (dataPackage: IDataPackage): Observable<IDataPackage> => {
        return handlePut<IDataPackage>(`/dataPackages/true`, dataPackage);
    };

    const deleteDataPackage = (dataPackageId: string) => {
        return handleDelete(`/datapackages/${dataPackageId}`);
    };

    const addUpdateFile = (dataPackageId: string, fileDescriptor: IDataPackageFileDescriptor): Observable<IDataPackage> => {
        return handlePut<IDataPackage>(`/datapackages/${dataPackageId}/file`, fileDescriptor);
    };

    const createDoi = (dataPackageId: string): Observable<string> => {
        return handlePut<string>(`/dataPackages/${dataPackageId}/doicreation`, null);
    };

    const handleSubmitErrorWithFileCheck = (error: ReactNode, showFileNames: boolean, details: string) => {
        const textStyle: CSSProperties = {
            textAlign: 'left'
        };

        const errorString = error?.toString();
        const errorParts = errorString?.split('||');
        const prefix = errorParts ? errorParts[0] : '';
        const copyingFilesErr =
            'There was a problem copying the data for one of the studies for this request. ' +
            'Please notify the Vivli Technical team at support@vivli.org';
        const copyingFilesErrExpanded =
            copyingFilesErr + ' and include the following' + errorParts[errorParts.length - 1].replace('"', '.');

        if (errorParts.length > 1 && (prefix === '"!' || prefix === '!')) {
            // the server appends "|| Correlation Id - <guid>" as the last part, so it is not a file name
            const errorFileNames = errorParts.slice(2, -1).map((fileName) => fileName.replace(/\\r\\n/g, ''));

            if (showFileNames && errorParts.length >= 4) {
                //this is files missing
                modalApi.error(
                    <div style={textStyle}>
                        The following file(s) did not upload properly for {details}:
                        {errorFileNames.map((fileName, index) => (
                            <Fragment key={index}>
                                <br />
                                {fileName}
                            </Fragment>
                        ))}
                        <br />
                        Please delete those files, close the browser, re-open the Vivli platform and re-upload those files.
                    </div>
                );
                return;
            } else {
                //container is missing or generic message should be shown
                if (errorParts.length > 2) {
                    modalApi.error(copyingFilesErrExpanded);
                } else {
                    modalApi.error(copyingFilesErr);
                }

                return;
            }
        }

        modalApi.error(error);
    };

    return {
        getDataPackage,
        getStudyDocumentsDataPackage,
        getForAnonymousUser,
        getDataPackageSubmitStatus,
        updateDataPackage,
        deleteDataPackage,
        addUpdateFile,
        createDoi,
        handleSubmitErrorWithFileCheck
    };
};
